import { create } from 'zustand'
import { DataSourcesService, type DataSourceDetails } from '@/services/apiClient'

interface DataSourceState {
  // Cache of datasources by ID
  dataSources: Map<string, DataSourceDetails>
  // Loading states by ID
  loading: Map<string, boolean>
  // Error states by ID
  errors: Map<string, Error | null>
  // Track active requests to prevent duplicates
  activeRequests: Map<string, Promise<DataSourceDetails>>
  
  // Actions
  fetchDataSource: (id: string) => Promise<DataSourceDetails | null>
  clearDataSource: (id: string) => void
  clearAll: () => void
}

export const useDataSourceStore = create<DataSourceState>((set, get) => ({
  dataSources: new Map(),
  loading: new Map(),
  errors: new Map(),
  activeRequests: new Map(),
  
  fetchDataSource: async (id: string) => {
    if (!id) return null
    
    const state = get()
    
    // Return cached data if available
    const cached = state.dataSources.get(id)
    if (cached) {
      return cached
    }
    
    // Return active request if in progress
    const activeRequest = state.activeRequests.get(id)
    if (activeRequest) {
      console.log('[DataSourceStore] Reusing active request for:', id)
      return activeRequest
    }
    
    // Set loading state
    set((state) => ({
      loading: new Map(state.loading).set(id, true),
      errors: new Map(state.errors).set(id, null)
    }))
    
    // Create new request
    const requestPromise = DataSourcesService.getDatasources1({ datasourceId: id })
      .then((data) => {
        // Store in cache
        set((state) => ({
          dataSources: new Map(state.dataSources).set(id, data),
          loading: new Map(state.loading).set(id, false),
          activeRequests: new Map(state.activeRequests)
        }))
        
        // Remove from active requests
        const newActiveRequests = new Map(get().activeRequests)
        newActiveRequests.delete(id)
        set({ activeRequests: newActiveRequests })
        
        return data
      })
      .catch((error: unknown) => {
        // Handle error
        set((state) => ({
          errors: new Map(state.errors).set(id, error instanceof Error ? error : new Error(String(error))),
          loading: new Map(state.loading).set(id, false)
        }))
        
        // Remove from active requests
        const newActiveRequests = new Map(get().activeRequests)
        newActiveRequests.delete(id)
        set({ activeRequests: newActiveRequests })
        
        throw error
      })
    
    // Store active request
    set((state) => ({
      activeRequests: new Map(state.activeRequests).set(id, requestPromise)
    }))
    
    return requestPromise
  },
  
  clearDataSource: (id: string) => {
    set((state) => {
      const newDataSources = new Map(state.dataSources)
      const newLoading = new Map(state.loading)
      const newErrors = new Map(state.errors)
      const newActiveRequests = new Map(state.activeRequests)
      
      newDataSources.delete(id)
      newLoading.delete(id)
      newErrors.delete(id)
      newActiveRequests.delete(id)
      
      return {
        dataSources: newDataSources,
        loading: newLoading,
        errors: newErrors,
        activeRequests: newActiveRequests
      }
    })
  },
  
  clearAll: () => {
    set({
      dataSources: new Map(),
      loading: new Map(),
      errors: new Map(),
      activeRequests: new Map()
    })
  }
}))

// Utility function for non-hook usage
export async function fetchDataSourceDirect(id: string): Promise<DataSourceDetails | null> {
  return useDataSourceStore.getState().fetchDataSource(id)
}